import type { AuditoriaResponse } from '@/api/generated/model';
import {
  formatarValor,
  lerDiff,
  ROTULO_ACAO,
  rotuloDaEntidade,
  VALOR_OCULTO,
} from '@/features/admin/auditoria/rotulos';
import { EstadoVazio } from '@/shared/components/estados';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';

interface Props {
  aberto: boolean;
  aoFechar: () => void;
  registro: AuditoriaResponse;
  ambienteNome?: string;
}

/**
 * Detalhe de uma linha da trilha.
 *
 * Um dialog, e não uma linha expansível na tabela: o diff não é a única coisa
 * que não cabe na grade — `ip`, `userAgent` e o `entidadeId` completo também
 * precisam de lugar, e o dialog carrega os quatro sem inflar a listagem.
 *
 * Sem query própria: o registro inteiro já veio na listagem.
 */
export function AlteracoesDialogo({ aberto, aoFechar, registro, ambienteNome }: Props) {
  const diff = lerDiff(registro.alteracoes);

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {ROTULO_ACAO[registro.acao]} — {rotuloDaEntidade(registro.entidade)}
          </DialogTitle>
          <DialogDescription>
            {new Date(registro.criadoEm).toLocaleString('pt-BR')} por{' '}
            {registro.autorNome ?? 'Sistema'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <dl className="rounded-lg bg-muted/50 px-4 py-3 space-y-2 text-sm">
            <Meta rotulo="Registro" valor={registro.entidadeId} mono />
            <Meta rotulo="Ambiente" valor={ambienteNome ?? (registro.tenantId ? null : 'Global')} />
            <Meta rotulo="Origem" valor={registro.ip} mono />
            <Meta rotulo="Cliente" valor={registro.userAgent} />
          </dl>

          {diff ? (
            <div className="overflow-x-auto max-h-80">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Campo</TableHead>
                    <TableHead>Antes</TableHead>
                    <TableHead>Depois</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.entries(diff).map(([campo, [antes, depois]]) => (
                    <TableRow key={campo}>
                      <TableCell className="font-medium text-foreground align-top whitespace-nowrap">
                        {campo}
                      </TableCell>
                      <TableCell className="align-top">
                        <Valor valor={antes} />
                      </TableCell>
                      <TableCell className="align-top">
                        <Valor valor={depois} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <EstadoVazio
              titulo="Sem diff registrado"
              descricao="Esta ação não guardou antes e depois — o estado atual está na tela da própria entidade."
            />
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={aoFechar}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Meta({ rotulo, valor, mono }: { rotulo: string; valor?: string | null; mono?: boolean }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-muted-foreground shrink-0">{rotulo}</dt>
      <dd className={`text-foreground text-right break-all ${mono ? 'font-mono text-xs' : ''}`}>
        {valor ?? '—'}
      </dd>
    </div>
  );
}

/**
 * Campo sensível vira um badge, não o texto `[oculto]` solto: deixa explícito
 * que o servidor redigiu o valor de propósito, em vez de parecer que o dado
 * gravado era literalmente essa string.
 */
function Valor({ valor }: { valor: unknown }) {
  if (valor === VALOR_OCULTO) return <Badge variant="outline">oculto</Badge>;

  return (
    <pre className="text-xs whitespace-pre-wrap break-all font-sans text-foreground">
      {formatarValor(valor)}
    </pre>
  );
}
